/**
 * ElevenLabs Speech-to-Text Service
 * Uses ElevenLabs Scribe model for high-quality transcription
 */

export interface ElevenLabsSTTOptions {
  language?: string;
}

export interface TranscriptionResult {
  text: string;
  language?: string;
}

class ElevenLabsSTTService {
  private baseUrl = 'https://api.elevenlabs.io/v1';
  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: Blob[] = [];
  private stream: MediaStream | null = null;
  private isCurrentlyRecording: boolean = false;

  private getApiKey(): string | null {
    // Check localStorage first (for user-provided keys)
    const userKey = localStorage.getItem('elevenlabs_api_key');
    if (userKey) return userKey;

    // Check environment variable
    return process.env.REACT_APP_ELEVENLABS_API_KEY || null;
  }

  isAvailable(): boolean {
    return this.getApiKey() !== null;
  }

  isRecording(): boolean {
    return this.isCurrentlyRecording;
  }

  /**
   * Start recording audio from the microphone
   */
  async startRecording(): Promise<void> {
    if (!this.isAvailable()) {
      throw new Error('STT API key not configured');
    }

    // Cleanup any previous recording
    this.cleanup();

    try {
      // Request microphone access
      this.stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000, // ElevenLabs works well with 16kHz
        } 
      });

      this.audioChunks = [];

      // Create MediaRecorder with webm/opus codec (good quality, small size)
      const mimeType = this.getSupportedMimeType();
      this.mediaRecorder = new MediaRecorder(this.stream, { mimeType });

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      this.mediaRecorder.start(100); // Collect data every 100ms
      this.isCurrentlyRecording = true;
      console.log('🎤 STT: Recording started');
    } catch (error: any) {
      this.cleanup();
      if (error.name === 'NotAllowedError') {
        throw new Error('Microphone permission denied');
      }
      throw new Error(`Failed to start recording: ${error.message}`);
    }
  }

  /**
   * Stop recording and transcribe the audio using ElevenLabs
   */
  async stopRecordingAndTranscribe(options: ElevenLabsSTTOptions = {}): Promise<TranscriptionResult> {
    console.log('🎤 STT: Stopping recording...', {
      isCurrentlyRecording: this.isCurrentlyRecording,
      mediaRecorderState: this.mediaRecorder?.state,
      chunksCount: this.audioChunks.length
    });

    if (!this.isCurrentlyRecording || !this.mediaRecorder) {
      throw new Error('No active recording');
    }

    return new Promise((resolve, reject) => {
      if (!this.mediaRecorder) {
        reject(new Error('No active recording'));
        return;
      }

      // Set up the onstop handler BEFORE calling stop()
      this.mediaRecorder.onstop = async () => {
        console.log('🎤 STT: MediaRecorder stopped, processing audio...', {
          chunksCount: this.audioChunks.length,
          totalSize: this.audioChunks.reduce((acc, chunk) => acc + chunk.size, 0)
        });

        try {
          const audioBlob = new Blob(this.audioChunks, { type: this.getSupportedMimeType() });
          
          console.log('🎤 STT: Audio blob size:', audioBlob.size);

          // Check if we have any audio data
          if (audioBlob.size < 1000) {
            throw new Error('Recording too short');
          }

          const result = await this.transcribeAudio(audioBlob, options);
          console.log('🎤 STT: Transcription result:', result);
          resolve(result);
        } catch (error) {
          console.error('🎤 STT: Error processing audio:', error);
          reject(error);
        } finally {
          this.cleanup();
        }
      };

      // Now stop the recorder
      this.mediaRecorder.stop();
      this.isCurrentlyRecording = false;
    });
  }

  /**
   * Cancel recording without transcribing
   */
  cancelRecording(): void {
    console.log('🎤 STT: Cancelling recording');
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.onstop = null; // Remove any existing handler
      this.mediaRecorder.stop();
    }
    this.isCurrentlyRecording = false;
    this.cleanup();
  }

  /**
   * Transcribe audio blob using ElevenLabs Speech-to-Text API
   */
  private async transcribeAudio(audioBlob: Blob, options: ElevenLabsSTTOptions = {}): Promise<TranscriptionResult> {
    const apiKey = this.getApiKey();
    if (!apiKey) {
      throw new Error('STT API key not configured');
    }

    // Convert blob to the right format if needed
    const audioFile = await this.convertToSupportedFormat(audioBlob);

    const formData = new FormData();
    // 'file' is the correct field name for the audio file
    formData.append('file', audioFile, 'recording.webm');
    // 'model_id' is REQUIRED - use scribe_v1 for production
    formData.append('model_id', 'scribe_v1');
    
    // Set language hint if provided (ISO-639-1 code like 'pt', 'en', 'es')
    if (options.language) {
      formData.append('language_code', options.language);
    }

    console.log('🎤 STT: Sending to API...', {
      fileSize: audioFile.size,
      fileType: audioFile.type,
      language: options.language
    });

    try {
      // ElevenLabs Speech-to-Text endpoint
      const response = await fetch(`${this.baseUrl}/speech-to-text`, {
        method: 'POST',
        headers: {
          'xi-api-key': apiKey,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('STT error response:', response.status, errorText);
        
        if (response.status === 401) {
          throw new Error('Invalid STT API key');
        } else if (response.status === 429) {
          throw new Error('Rate limit exceeded. Please try again later.');
        } else if (response.status === 400 || response.status === 422) {
          // Parse error details
          try {
            const errorJson = JSON.parse(errorText);
            const detail = errorJson.detail;
            if (typeof detail === 'string') {
              throw new Error(`API Error: ${detail}`);
            } else if (detail?.message) {
              throw new Error(`API Error: ${detail.message}`);
            } else if (Array.isArray(detail)) {
              throw new Error(`API Error: ${detail.map((d: any) => d.msg || d.message).join(', ')}`);
            }
            throw new Error(`Transcription failed: ${errorText}`);
          } catch (parseError) {
            if (parseError instanceof Error && parseError.message.startsWith('API Error')) {
              throw parseError;
            }
            throw new Error(`Transcription failed: ${errorText}`);
          }
        }
        throw new Error(`Transcription failed: ${response.status}`);
      }

      const data = await response.json();
      console.log('🎤 STT: API response:', data);
      
      return {
        text: data.text || '',
        language: data.language_code || data.detected_language,
      };
    } catch (error: any) {
      if (error.message.includes('Failed to fetch')) {
        throw new Error('Network error. Please check your connection.');
      }
      throw error;
    }
  }

  /**
   * Get supported MIME type for recording
   */
  private getSupportedMimeType(): string {
    const types = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4',
      'audio/ogg;codecs=opus',
    ];

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }

    return 'audio/webm'; // Fallback
  }

  /**
   * Convert audio blob to a supported format if needed
   */
  private async convertToSupportedFormat(blob: Blob): Promise<File> {
    // ElevenLabs accepts webm, mp3, mp4, wav, etc.
    // Our MediaRecorder produces webm which is supported
    return new File([blob], 'recording.webm', { type: blob.type });
  }

  /**
   * Cleanup resources
   */
  private cleanup(): void {
    console.log('🎤 STT: Cleaning up resources');
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }
    this.mediaRecorder = null;
    this.audioChunks = [];
    this.isCurrentlyRecording = false;
  }
}

// Export singleton instance
export const elevenLabsSTT = new ElevenLabsSTTService();
export default elevenLabsSTT;
